import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { Instagram, Send } from 'lucide-react';

// Bandeja de Instagram (separada de WhatsApp). Lee instagram_messages, agrupa por igsid,
// muestra el hilo y permite responder (dentro de la ventana de 24h) via instagram-send-text.

interface IgMsg { id: string; igsid: string; username: string | null; direction: string; is_agent: boolean; is_escalation: boolean; message_text: string; status: string; created_at: string; }

function fmtTime(s: string) { try { return new Date(s).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } }

export function InstagramInbox({ showSnackbar }: { showSnackbar: (m: string, t: 'success' | 'error') => void }) {
  const [msgs, setMsgs] = useState<IgMsg[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const lastTsRef = useRef<string | null>(null); // cursor del refresco incremental

  // Igual que la bandeja de WhatsApp: el polling trae solo lo nuevo, no los 3000 mensajes.
  async function load(opts?: { incremental?: boolean }) {
    const since = lastTsRef.current;
    const inc = !!opts?.incremental && !!since;
    const { data } = inc
      ? await supabase.from('instagram_messages').select('*').gte('created_at', since!).order('created_at', { ascending: true }).limit(500)
      : await supabase.from('instagram_messages').select('*').order('created_at', { ascending: true }).limit(3000);
    const rows = (data || []) as IgMsg[];
    if (rows.length) {
      const maxTs = rows[rows.length - 1].created_at;
      if (!lastTsRef.current || maxTs > lastTsRef.current) lastTsRef.current = maxTs;
    }
    if (inc) {
      setMsgs(prev => {
        const seen = new Set(prev.map(m => m.id));
        const fresh = rows.filter(r => !seen.has(r.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
    } else {
      setMsgs(rows);
    }
    setLoading(false);
  }
  useEffect(() => { setLoading(true); load(); const iv = setInterval(() => load({ incremental: true }), 20000); return () => clearInterval(iv); }, []);

  const convs = useMemo(() => {
    const m = new Map<string, IgMsg[]>();
    for (const x of msgs) { if (!m.has(x.igsid)) m.set(x.igsid, []); m.get(x.igsid)!.push(x); }
    const arr = Array.from(m.entries()).map(([igsid, list]) => {
      const visible = list.filter(x => !x.is_escalation);
      const last = visible[visible.length - 1] || list[list.length - 1];
      const name = list.find(x => x.username)?.username || null;
      const unanswered = !!last && last.direction === 'incoming' && /reacci|standby/i.test(last.status) === false;
      const escalated = list.some(x => x.is_escalation) && !list.some(x => x.direction === 'outgoing' && !x.is_agent && new Date(x.created_at) > new Date([...list].reverse().find(y => y.is_escalation)!.created_at));
      return { igsid, list, last, name, unanswered, escalated, t: last ? new Date(last.created_at).getTime() : 0 };
    });
    arr.sort((a, b) => b.t - a.t);
    return arr;
  }, [msgs]);

  useEffect(() => { if ((!selected || !convs.find(c => c.igsid === selected)) && convs.length) setSelected(convs[0].igsid); }, [convs, selected]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [selected, msgs.length]);

  const sel = selected ? convs.find(c => c.igsid === selected) : null;

  async function send() {
    if (!sel || !reply.trim() || sending) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('instagram-send-text', { body: { igsid: sel.igsid, text: reply.trim() } });
      if (error || !data?.ok) showSnackbar(data?.outside_window ? 'Fuera de la ventana de 24h: la persona tiene que escribirte primero.' : `No se pudo enviar: ${data?.error || 'error'}`, 'error');
      else { setReply(''); showSnackbar('Enviado', 'success'); load(); }
    } catch { showSnackbar('Error al enviar', 'error'); }
    setSending(false);
  }

  return (
    <div className="ibx">
      <div className="ibx-list">
        <div className="ibx-filters"><div style={{ padding: '8px 12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, color: '#c13584' }}><Instagram size={16} /> Instagram · @krilic.studio</div></div>
        <div className="ibx-convs">
          {loading ? <div className="ibx-empty">Cargando…</div> : convs.length === 0 ? <div className="ibx-empty">Sin conversaciones de Instagram.</div> : convs.map(c => (
            <div key={c.igsid} className={`ibx-conv ${selected === c.igsid ? 'active' : ''}`} onClick={() => setSelected(c.igsid)}>
              <div className="ibx-conv-top">
                <span className="ibx-conv-name">{c.name ? '@' + c.name : c.igsid.slice(-8)}</span>
                <span className="ibx-conv-time">{c.last ? fmtTime(c.last.created_at) : ''}</span>
              </div>
              <div className="ibx-conv-preview">
                {c.escalated && <span className="ibx-tag esc">⚠ Escalado</span>}
                {(c.last?.message_text || '').slice(0, 46)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ibx-thread-wrap">
        {!sel ? <div className="ibx-empty">Elegí una conversación.</div> : (
          <>
            <div className="ibx-thread-head">
              <span className="ibx-conv-name">{sel.name ? '@' + sel.name : sel.igsid}</span>
              {sel.name && <a href={`https://instagram.com/${sel.name}`} target="_blank" rel="noreferrer" className="ibx-tag" style={{ textDecoration: 'none' }}>ver perfil ↗</a>}
            </div>
            <div className="ibx-thread" ref={threadRef}>
              {sel.list.map(m => m.is_escalation ? (
                <div key={m.id} className="ibx-sysnote">⚠ {m.message_text}</div>
              ) : (
                <div key={m.id} className={`ibx-msg ${m.direction}`}>
                  <div className={`ibx-bubble ${m.direction} ${m.status === 'failed' ? 'failed' : ''} ${m.is_agent ? 'agent' : ''}`}>
                    <div className="ibx-text">{m.message_text}</div>
                    <div className="ibx-time">{m.direction === 'outgoing' ? (m.is_agent ? '🤖 ' : '🧑 ') : ''}{fmtTime(m.created_at)}{m.status === 'failed' ? ' · falló' : ''}{m.status === 'standby' ? ' · (ManyChat)' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ibx-reply">
              <textarea value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Responder por Instagram… (dentro de 24h)" rows={2} />
              <button className="ibx-send ig" onClick={send} disabled={sending || !reply.trim()}><Send size={16} /> {sending ? '…' : 'Enviar'}</button>
            </div>
            <div className="ibx-note">Texto libre solo dentro de las 24h del último mensaje. Si respondés vos, el agente no interviene en ese chat.</div>
          </>
        )}
      </div>
    </div>
  );
}

export default InstagramInbox;
